import LegalLayout from '@/components/layout/LegalLayout';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function GdprPage() {
  useSEO({
    title: 'GDPR Statement | Acodia',
    description: "Acodia's commitment to UK GDPR: lawful basis, your rights, how to exercise them, and our 30-day response commitment.",
    path: '/gdpr',
    ogImage: `${SITE_URL}/images/og/og-default.png`,
  });

  return (
    <LegalLayout eyebrow="Legal" title="GDPR Statement" lastUpdated="26 May 2026" testId="gdpr-page">
      <h2>1. Our Commitment</h2>
      <p>
        Acodia Ltd is committed to handling personal data in accordance with the UK General Data Protection
        Regulation (UK GDPR) and the Data Protection Act 2018. This statement summarises your rights and how
        we meet our obligations. For the full detail, see our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>2. Your Rights at a Glance</h2>
      <ul>
        <li><strong>Right to be informed</strong> — to know how and why we use your data (set out in our Privacy Policy).</li>
        <li><strong>Right of access</strong> — to request a copy of the personal data we hold about you.</li>
        <li><strong>Right to rectification</strong> — to have inaccurate or incomplete data corrected.</li>
        <li><strong>Right to erasure</strong> — to request deletion of your data where there is no overriding reason to keep it.</li>
        <li><strong>Right to restrict processing</strong> — to limit how we use your data in certain circumstances.</li>
        <li><strong>Right to data portability</strong> — to receive your data in a structured, machine-readable format.</li>
        <li><strong>Right to object</strong> — to object to processing based on our legitimate interests.</li>
        <li><strong>Rights around automated decision-making</strong> — we do not make solely automated decisions that produce legal or similarly significant effects about you.</li>
      </ul>

      <h2>3. How to Exercise Your Rights</h2>
      <p>
        Email <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a> with your request. We will
        respond within <strong>30 days</strong> (one month), as required by UK GDPR. We may ask you to verify
        your identity before we act on a request.
      </p>

      <h2>4. Lawful Bases We Rely On</h2>
      <ul>
        <li><strong>Contract</strong> — to provide the Acodia Watch service you subscribe to.</li>
        <li><strong>Legitimate interests</strong> — security, fraud prevention, and improving our service.</li>
        <li><strong>Consent</strong> — for any optional marketing, withdrawable at any time.</li>
        <li><strong>Legal obligation</strong> — for tax, accounting, and regulatory records.</li>
      </ul>

      <h2>5. Data Protection Officer</h2>
      <p>
        Acodia Ltd does not require a formal Data Protection Officer under UK GDPR Article 37. Our data
        protection contact is <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>.
      </p>

      <h2>6. International Transfers &amp; Subprocessors</h2>
      <p>
        We use a small number of trusted subprocessors, some based outside the UK. Non-UK transfers are
        protected by Standard Contractual Clauses and equivalent safeguards. The full list is in our{' '}
        <a href="/privacy">Privacy Policy</a>. We do not sell or share your personal data for advertising.
      </p>

      <h2>7. Complaints</h2>
      <p>
        If you have a concern about how we handle your data, please contact us first. You also have the right
        to complain to the UK Information Commissioner's Office (ICO) at{' '}
        <a href="https://ico.org.uk/make-a-complaint/">ico.org.uk/make-a-complaint</a>.
      </p>

      <h2>8. Company Details</h2>
      <p>
        Acodia Ltd, 20 Wenlock Road, London, N1 7GU, United Kingdom.<br />
        Companies House: 15412512<br />
        ICO Registration: ZB786356
      </p>
      <hr />
      <p>Have questions? Email <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>.</p>
    </LegalLayout>
  );
}
